import React, { useEffect } from 'react';
import { useTenantsApp } from '../../Context/TenantsApp/TenantsAppContext';
import { DELETE_TENANT, SELECT_ALL_TENANT_PROD, SELECT_ALL_TENANT_TEST, SET_TENANT_ID, TOGGLE_DELETE_APP_DIALOG, TOGGLE_SYNC_ALL_TENANTS_DIALOG, UPDATE_TENANT_BY_TENANT_ID } from './TenantsAppEvents';
import { io } from 'socket.io-client';
import { usePropsStoreValue } from '../../Hooks/PropStore/usePropsStore';
import TenantCard from './TenantCard';
import { Checkbox } from '@progress/kendo-react-inputs';
import Scrollable from '../Shared/Edit/Scrollable';
import { useTranslation } from 'react-i18next';

const TenantsAppGrid = () => {

    const { t } = useTranslation([
        "tenant/tenant",
    ], {
        useSuspense: false
    })

    const { state, dispatch } = useTenantsApp();

    const project = usePropsStoreValue("project");

    const { isActive, hasSchemaChanges } = state.project;
    const showSchemaAlert = !isActive || hasSchemaChanges;

    const shouldSyncEnabled = state.tenants.some(tenant => tenant.syncToProd || tenant.syncToTest);
    const isCurrentlySyncing = state.tenants.some(tenant => tenant.syncStatus === 'In Progress' || tenant.syncStatus === 'Queued');
    const inProgress = state.tenants.some(tenant => tenant.syncStatus === 'In Progress');

    // Listen for Socket.io events from BullMQ
    useEffect(() => {
        const url = process.env.REACT_APP_DESIGNER_API_URL
        const socket = io(url);
        socket.on('updateTenants', (update) => {
            if (project._id !== update.masterProjectId) return;
            for (const tenant of update.tenants) {
                dispatch({
                    type: UPDATE_TENANT_BY_TENANT_ID,
                    payload: {
                        tenantId: tenant.tenantId,
                        data: {
                            syncStatus: tenant.syncStatus,
                            lastSyncedAt: tenant.lastSyncedAt,
                            error: tenant.error,
                            syncToProd: tenant.syncToProd,
                            syncToTest: tenant.syncToTest
                        }
                    }
                });
            }
        });

        socket.on('deleteTenant', (update) => {
            if (!state.showDeleteAppDialog) {
                dispatch({ type: SET_TENANT_ID, payload: null });
                dispatch({ type: TOGGLE_DELETE_APP_DIALOG });
                dispatch({
                    type: DELETE_TENANT,
                    payload: update.tenantId
                })
            }
        });

        // Cleanup event listeners on unmount
        return () => {
            socket.off('tenantUpdate');
            socket.off('deleteTenant');
        };
        // eslint-disable-next-line
    }, []);

    return (
        <div className="d-flex flex-column h-100">
            <div className="d-flex flex-wrap align-items-center bg-white">
                <div className="me-auto d-flex flex-wrap gap-2">
                    <button
                        className="btn btn-secondary me-2"
                        onClick={() => dispatch({ type: SELECT_ALL_TENANT_PROD, payload: !state.tenants.every(tenant => tenant.syncToProd) })}>
                        <Checkbox
                            disabled={!shouldSyncEnabled || isCurrentlySyncing || inProgress}
                            value={state.tenants.every(tenant => tenant.syncToProd)}
                            onChange={(e) => dispatch({ type: SELECT_ALL_TENANT_PROD, payload: e.target.value })}
                            className="sync-checkbox"
                        />
                        {t("select_production")}
                    </button>
                    <button
                        className="btn btn-secondary"
                        onClick={() => dispatch({ type: SELECT_ALL_TENANT_TEST, payload: !state.tenants.every(tenant => tenant.syncToTest) })}>
                        <Checkbox
                            disabled={!shouldSyncEnabled || isCurrentlySyncing || inProgress}
                            value={state.tenants?.every(tenant => tenant.syncToTest)}
                            onChange={(e) => dispatch({ type: SELECT_ALL_TENANT_TEST, payload: e.target.value })}
                            className="sync-checkbox"
                        />
                        {t("select_test")}
                    </button>
                </div>
                <div className="col d-flex justify-content-end">
                    <button
                        onClick={() => dispatch({ type: TOGGLE_SYNC_ALL_TENANTS_DIALOG })}
                        disabled={!shouldSyncEnabled || isCurrentlySyncing || inProgress || showSchemaAlert}
                        className="btn btn-primary"
                    >
                        <i className={`fas fa-sync me-2${(isCurrentlySyncing || inProgress) ? " fa-spin" : ""}`}></i>
                        {t("sync_tenants")}
                    </button>
                </div>
            </div>

            {/* Tenant Cards Grid */}
            <Scrollable>
                <div className="flex-grow-1 overflow-y-auto pt-3" style={{ minHeight: 0, height: '100%' }}>
                    <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-4">
                        {state.tenants.map((tenant) => (
                            <div key={tenant._id}>
                                <TenantCard tenantId={tenant._id} />
                            </div>
                        ))}
                    </div>
                </div>
            </Scrollable>
        </div>
    )
}

export default TenantsAppGrid;